/**
 * Smart Header - Hide on scroll down, show on scroll up
 * 
 * Adds/removes classes on the header based on scroll direction
 * to create a smooth hide/show effect that maximizes content visibility
 * while keeping navigation accessible when scrolling up.
 * 
 * Also dynamically adapts header theme (light/dark) based on the
 * background color of the section currently in viewport.
 */

class SmartHeader {
  constructor() {
    this.header = document.getElementById('masthead');
    
    if (!this.header) {
      return;
    }

    this.lastScrollTop = 0;
    this.scrollThreshold = 5; // Minimum scroll distance to trigger change
    this.scrollOffset = 100; // Don't hide header until scrolled this many pixels from top
    this.isScrolling = false;
    this.currentTheme = null; // Track current theme to avoid unnecessary updates
    
    // Cache header elements for theme switching
    this.logo = this.header.querySelector('.site-logo');
    this.menuIcon = this.header.querySelector('.menu-icon svg');
    
    // Logo paths
    this.logoLight = this.logo ? this.logo.src.replace('logo-dark.svg', 'logo-light.svg').replace('logo-light.svg', 'logo-light.svg') : null;
    this.logoDark = this.logo ? this.logo.src.replace('logo-light.svg', 'logo-dark.svg').replace('logo-dark.svg', 'logo-dark.svg') : null;
    
    this.init();
  }

  init() {
    // Use requestAnimationFrame for smooth performance
    window.addEventListener('scroll', () => {
      if (!this.isScrolling) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          this.isScrolling = false;
        });
        this.isScrolling = true;
      }
    }, { passive: true });

    // Also check on page load in case user refreshes mid-page
    this.handleScroll();
    
    // Set initial theme based on current header state
    if (this.header.classList.contains('header-dark')) {
      this.currentTheme = 'dark';
    } else if (this.header.classList.contains('header-light-green')) {
      this.currentTheme = 'light-green';
    } else if (this.header.classList.contains('header-light')) {
      this.currentTheme = 'light';
    }
  }

  /**
   * Detect background color of section in viewport
   * Returns 'dark' or 'light' based on section background
   */
  detectSectionTheme() {
    // Get header height to calculate proper viewport position
    const headerHeight = this.header.offsetHeight;
    const viewportCheckpoint = headerHeight + 50; // Check slightly below header
    
    // Get element at checkpoint position
    const elementAtCheckpoint = document.elementFromPoint(
      window.innerWidth / 2,
      viewportCheckpoint
    );
    
    if (!elementAtCheckpoint) {
      return this.currentTheme || 'light'; // Fallback to current or default
    }
    
    // Walk up the DOM tree to find a section or main content area
    let section = elementAtCheckpoint;
    let maxDepth = 10; // Prevent infinite loops
    
    while (section && maxDepth > 0) {
      // Check if this is a section-level element
      if (section.tagName === 'SECTION' || 
          section.tagName === 'MAIN' || 
          section.classList.contains('site-main') ||
          section.id === 'primary') {
        break;
      }
      section = section.parentElement;
      maxDepth--;
    }
    
    if (!section) {
      return this.currentTheme || 'light';
    }
    
    // Get computed background color
    const bgColor = window.getComputedStyle(section).backgroundColor;
    
    // Parse RGB values
    const rgb = bgColor.match(/\d+/g);
    if (!rgb || rgb.length < 3) {
      return this.currentTheme || 'light';
    }
    
    const r = parseInt(rgb[0]);
    const g = parseInt(rgb[1]);
    const b = parseInt(rgb[2]);
    
    // Special case: Lichen color (#C7CCB6 = RGB 199, 204, 182)
    // Use light-green header with dark elements
    const isLichen = Math.abs(r - 199) < 20 && 
                     Math.abs(g - 204) < 20 && 
                     Math.abs(b - 182) < 20;
    
    if (isLichen) {
      return 'light-green'; // Use light-green header for lichen backgrounds
    }
    
    // Calculate relative luminance (WCAG formula)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Dark backgrounds (pine, midnight, shadow-pine) have low luminance
    // Light backgrounds (buttermilk) have high luminance
    // Threshold of 0.5 works well for our color palette
    return luminance > 0.5 ? 'light' : 'dark';
  }

  /**
   * Apply theme to header (dark, light, or light-green)
   */
  applyHeaderTheme(theme) {
    if (theme === this.currentTheme) {
      return; // No change needed
    }
    
    this.currentTheme = theme;
    
    // Remove all theme classes first
    this.header.classList.remove('header-dark', 'header-light', 'header-light-green');
    
    if (theme === 'dark') {
      // Dark background (pine) - use light elements
      this.header.classList.add('header-dark');
      
      // Swap logo to light version
      if (this.logo && this.logoLight) {
        this.logo.src = this.logoLight;
      }
      
      // Change menu icon to lichen color
      if (this.menuIcon) {
        const paths = this.menuIcon.querySelectorAll('path');
        paths.forEach(path => {
          path.setAttribute('fill', '#C7CCB6'); // lichen
        });
      }
    } else if (theme === 'light-green') {
      // Light green background (lichen) - use dark elements
      this.header.classList.add('header-light-green');
      
      // Swap logo to dark version
      if (this.logo && this.logoDark) {
        this.logo.src = this.logoDark;
      }
      
      // Change menu icon to dark pine color
      if (this.menuIcon) {
        const paths = this.menuIcon.querySelectorAll('path');
        paths.forEach(path => {
          path.setAttribute('fill', '#343B30'); // pine
        });
      }
    } else {
      // Light background (buttermilk) - use burnt-orange elements
      this.header.classList.add('header-light');
      
      // Swap logo to dark version
      if (this.logo && this.logoDark) {
        this.logo.src = this.logoDark;
      }
      
      // Change menu icon to burnt-orange color
      if (this.menuIcon) {
        const paths = this.menuIcon.querySelectorAll('path');
        paths.forEach(path => {
          path.setAttribute('fill', '#7D3904'); // burnt-orange
        });
      }
    }
  }

  handleScroll() {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // At the top of the page - always show header
    if (currentScrollTop <= this.scrollOffset) {
      this.header.classList.remove('header-hidden');
      this.header.classList.remove('header-scrolled');
      this.lastScrollTop = currentScrollTop;
      return;
    }

    // Determine scroll direction
    const scrollDelta = currentScrollTop - this.lastScrollTop;
    
    // Only trigger if scroll delta exceeds threshold (prevents jitter)
    if (Math.abs(scrollDelta) < this.scrollThreshold) {
      return;
    }

    // Add scrolled state for styling changes (shadow, etc.)
    this.header.classList.add('header-scrolled');

    if (scrollDelta > 0) {
      // Scrolling DOWN - hide header
      this.header.classList.add('header-hidden');
    } else {
      // Scrolling UP - show header and adapt to current section
      this.header.classList.remove('header-hidden');
      
      // Detect and apply section theme when header becomes visible
      const sectionTheme = this.detectSectionTheme();
      this.applyHeaderTheme(sectionTheme);
    }

    this.lastScrollTop = currentScrollTop;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SmartHeader();
  });
} else {
  new SmartHeader();
}

export default SmartHeader;
